import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FoilCard } from "@/components/FoilCard";
import type { Ss01Card } from "@/lib/ss01-cards";

type OpenCard = { src: string; alt: string; label: string; foil: boolean } | null;

export function Ss01CardPair({ card }: { card: Ss01Card }) {
  const [open, setOpen] = useState<OpenCard>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(null);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);

  const versions = [
    { src: card.image, alt: `${card.nameEn} ${card.code} base card`, label: "Base card", foil: false },
    { src: card.parallel, alt: `${card.nameEn} ${card.code} parallel foil card`, label: "Parallel foil", foil: true },
  ];

  return (
    <>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-5">
        {versions.map((version) => (
          <button
            key={version.label}
            type="button"
            onClick={() => setOpen(version)}
            aria-label={`Enlarge ${version.alt}`}
            className="group min-w-0 rounded-lg border border-border/60 bg-background/50 p-2 text-left transition hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <FoilCard src={version.src} alt={version.alt} foil={version.foil} className="mx-auto w-full max-w-56 rounded-lg" />
            <span className="mt-2 block text-center text-xs font-semibold text-foreground group-hover:text-primary">
              {version.label} · Click to enlarge
            </span>
          </button>
        ))}
      </div>

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={open.alt}
          className="fixed inset-0 z-50 grid place-items-center bg-background/95 p-4 backdrop-blur"
          onClick={() => setOpen(null)}
        >
          <div className="relative w-full max-w-md" onClick={(event) => event.stopPropagation()}>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={() => setOpen(null)}
              aria-label="Close enlarged card"
              className="absolute -right-2 -top-2 z-10 rounded-full"
            >
              <X />
            </Button>
            <FoilCard src={open.src} alt={`${open.alt}, enlarged`} foil={open.foil} loading="eager" className="mx-auto max-h-[82vh] w-auto max-w-full" />
            <p className="mt-2 text-center text-xs text-muted-foreground">{open.label} · Move across the card to view the foil effect</p>
          </div>
        </div>
      ) : null}
    </>
  );
}